// Conversation Memory Service
// Remembers user's previous questions and conversations for context-aware AI responses

import { logger } from '../utils/logger';

export interface ConversationMessage {
  role: 'user' | 'ai';
  message: string;
  timestamp: Date;
  context?: {
    testId?: string;
    questionId?: string;
    topic?: string;
  };
}

class ConversationMemory {
  private history: ConversationMessage[] = [];
  private readonly STORAGE_KEY = 'conversation_history';
  private readonly MAX_HISTORY = 20;

  constructor() {
    this.loadFromStorage();
  }

  // Store conversation message
  addMessage(role: 'user' | 'ai', message: string, context?: any): void {
    this.history.push({
      role,
      message,
      timestamp: new Date(),
      context
    });

    // Keep last MAX_HISTORY messages for context
    if (this.history.length > this.MAX_HISTORY) {
      this.history.shift();
    }

    // Persist to localStorage
    this.saveToStorage();
  }

  // Get recent context
  getRecentContext(limit: number = 10): ConversationMessage[] {
    return this.history.slice(-limit);
  }

  // Get user's previous questions about a topic
  getTopicHistory(topic: string): ConversationMessage[] {
    return this.history.filter(msg => 
      msg.context?.topic === topic && msg.role === 'user'
    );
  }

  // Get conversation history formatted for AI
  getFormattedHistory(limit: number = 10): string {
    const recent = this.getRecentContext(limit);
    return recent.map(msg => `${msg.role}: ${msg.message}`).join('\n');
  }

  // Clear history
  clearHistory(): void {
    this.history = [];
    this.saveToStorage();
    logger.info('Conversation history cleared');
  }

  // Get personalized greeting based on history
  getPersonalizedGreeting(): string {
    const recent = this.getRecentContext(5);
    const lastSession = recent.find(msg => msg.role === 'ai');
    
    if (lastSession && lastSession.context?.topic) {
      return `Welcome back! Last time we discussed ${lastSession.context.topic}. Ready to continue?`;
    }
    
    return "Welcome! I'm your AI driving theory tutor. Ready to learn?";
  }

  private saveToStorage(): void {
    try {
      const serialized = JSON.stringify(this.history.map(msg => ({
        ...msg,
        timestamp: msg.timestamp.toISOString()
      })));
      localStorage.setItem(this.STORAGE_KEY, serialized);
    } catch (error) {
      logger.error('Error saving conversation history:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.history = parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      logger.error('Error loading conversation history:', error);
      this.history = [];
    }
  }
}

export const conversationMemory = new ConversationMemory();















